import { Checkbox, ErrorText, FormFields, Heading, Link, MediumText } from "@RooUI";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext, useFormState } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { ControlledInput } from "../../Common/Controllers";
import { ControlledPasswordInput } from "../../Common/Controllers/ControlledPasswordInput";
import { ControlledPhoneInput } from "../../Common/Controllers/ControlledPhoneInput";
import { RegisterCTAText } from "../../Common/Form/RegisterCTAText";
import { MediaSourceOption } from "../../Common/Form/hooks/useMediaSourceOptions";
import { fetchMediaSourceOptions } from "../../Common/Form/service";
import { showToast } from "../../Common/Toasts/action/ToastActions";
import { ErrorResponse, isErrorResponse } from "../../api/common/error";
import { createHospital } from "../../api/hospital";
import { createUser } from "../../api/user/createUser";
import { getHasInvite } from "../../api/user/hasInvite";
import { SET_AUTHORISE_USER } from "../../constants/Login";
import { HOSPITAL_USER } from "../../constants/UserRoleConstants";
import * as UserType from "../../constants/UserTypeConstants";
import { anticipatedReliefNeedOptions } from "../../constants/dropDownConstants";
import HospitalRegistrationCreateAccountImg from "../../static/images/HospitalRegistrationCreateAccount.png";
import RooCircleImage from "../../static/images/roo-circle.svg";
import { Event, EventTracker } from "../../tracking/service/EventTracker/EventTrackerService";
import RegistrationPage from "../RegistrationPage";
import { HOSPITAL_REGISTRATION_LOCALSTORAGE_KEY } from "../constants";
import { useRegistrationContext } from "../registrationContext";
import { getHospitalUserAccountRegistrationFormSchema } from "../registrationForm";
import { ButtonContainer, Column, ContainerTopMargin, Image, RegistrationButton } from "../styles";
import { RegistrationForm } from "../types";
import { updateSingleToggleService } from "../../Common/NotificationSettings/NotificationSettingsUtils";
import * as Sentry from "@sentry/react";

import { ErrorContainer, TextContainer } from "./styles";

function CreateAccount() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { setRegistrationContext, registrationContext } = useRegistrationContext()!;

  const { handleSubmit, control, watch } = useFormContext<RegistrationForm>();
  const { errors } = useFormState<RegistrationForm>({ control });
  const [duplicateUserError, setDuplicateUserError] = useState(false);
  const [openInviteError, setOpenInviteError] = useState(false);
  const [mediaSourceOptions, setMediaSourceOptions] = useState<MediaSourceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [isMarketingSMSEnabled, setIsMarketingSMSEnabled] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      const options = await fetchMediaSourceOptions();
      setMediaSourceOptions(options);
    };

    fetchOptions();
  }, []);

  const onContinue = async (data: RegistrationForm) => {
    setLoading(true);
    const formData = { ...registrationContext, ...data };
    const hasInvite = await getHasInvite(formData.emailId!);
    if (hasInvite) {
      setOpenInviteError(true);
      setLoading(false);
      return;
    }
    let userId = localStorage.getItem("userId") ? Number(localStorage.getItem("userId")) : null;
    if (!userId || userId < 0) {
      const userResponse = await createUser({
        emailId: formData.emailId!,
        userTypeId: UserType.HOSPITAL_USER_TYPE,
        userRoleId: HOSPITAL_USER,
        isFromMobile: 0,
        firstName: formData.firstName!,
        lastName: formData.lastName!,
        mobilePhoneNumber: formData.mobilePhoneNumber!,
        password: formData.password!,
      });
      if (isErrorResponse(userResponse)) {
        if (
          (userResponse as ErrorResponse).name === "UsernameExistsException" ||
          (userResponse as ErrorResponse).message ===
            "An account with the given email already exists."
        ) {
          EventTracker.send({
            eventName: Event.Name.DUPLICATE_EMAIL_REGISTRATION_ATTEMPT,
            eventType: Event.Type.IMPRESSION,
            entityType: Event.Entity.HOSPITAL,
            entityId: undefined,
            context: {
              email: formData.emailId,
            },
          });
          setDuplicateUserError(true);
        } else {
          dispatch(
            showToast({ variant: "error", message: "Error", description: "Error creating account" })
          );
        }
        setLoading(false);
        return;
      }
      userId = userResponse.data.userId;
      dispatch({ type: SET_AUTHORISE_USER });
      localStorage.setItem("email", formData.emailId!);
      localStorage.setItem("userTypeId", String(formData.selectedUserType));
      localStorage.setItem("userId", String(userId));

      // Update marketing SMS preference if user opted in
      try {
        await updateSingleToggleService({
          userId,
          notificationCode: 19, // Marketing SMS notification code
          config: {
            smsEnabled: isMarketingSMSEnabled,
            emailEnabled: true,
            pushEnabled: true,
          },
        });
      } catch (error) {
        // Log error but don't block registration
        Sentry.captureException(error);
      }
    }

    const selectedMediaSource = mediaSourceOptions.find(
      (option) => option.value === formData?.hearAboutRoo
    )?.label;

    const anticipatedReliefNeed = anticipatedReliefNeedOptions.find(
      (option) => option.value === formData?.anticipatedReliefNeed
    )?.label;

    const hospitalResponse = await createHospital({
      userId: userId,
      hospitalName: formData.hospitalName!,
      hospitalAddress: formData.hospitalAddress!,
      hospitalState: formData.hospitalState!,
      hospitalCity: formData.hospitalCity!,
      hospitalZipcode: formData.hospitalZipcode!,
      typeOfHospital: formData.typeOfHospital!,
      hospitalPhoneNumber: formData.hospitalPhoneNumber!,
      hearAboutRoo: selectedMediaSource || "",
      selfIdentifiedEnterpriseName: formData.selfIdentifiedEnterpriseName,
      anticipatedReliefNeed,
    });

    if (isErrorResponse(hospitalResponse) || !hospitalResponse?.data) {
      dispatch(
        showToast({ variant: "error", message: "Error", description: "Error creating account" })
      );
      setLoading(false);
      return;
    }

    localStorage.setItem("hospitalId", String(hospitalResponse.data.hospitalId));

    const isRooState = hospitalResponse.data.rooState;

    setRegistrationContext({
      ...formData,
      calendlyLink: hospitalResponse.data.calendlyLink,
      hospitalId: hospitalResponse.data.hospitalId,
      isRooState: isRooState,
      userId: userId,
    });

    if (isRooState) {
      history.push({
        pathname: "/register/hospitalBookCall",
      });
    } else {
      history.push({
        pathname: "/register/success",
      });
    }
  };

  return (
    <RegistrationPage
      progressBarPercent={(3 / 4) * 100}
      title="Create your account"
      previousPage={{ title: "Hospital Profile", pathname: "/register/hospitalProfile" }}
      rightContainerContent={
        <>
          <Image src={HospitalRegistrationCreateAccountImg} />
          <TextContainer>
            <img src={RooCircleImage} />
            <div>
              <MediumText>
                “What we appreciate most is the ability to work with the people before making an
                offer. It gives us a chance to assess them and essentially do that working
                interview.”{" "}
              </MediumText>
              <Heading level={6}>Krista Fuld, CVPM Hospital Administrator</Heading>
              <Heading level={6}>Pet Medical Center of Duncanville</Heading>
            </div>
          </TextContainer>
        </>
      }
      leftContainerContent={
        <Column>
          <FormFields>
            <ControlledInput
              name="firstName"
              control={control}
              label="First name"
              error={errors?.firstName?.message}
              defaultValue={registrationContext.firstName}
            />
            <ControlledInput
              name="lastName"
              control={control}
              label="Last name"
              error={errors?.lastName?.message}
              defaultValue={registrationContext.lastName}
            />
            <ControlledPhoneInput
              name="mobilePhoneNumber"
              control={control}
              label="Mobile phone number"
              error={errors?.mobilePhoneNumber?.message}
              defaultValue={registrationContext.mobilePhoneNumber}
            />
            <ControlledInput
              name="emailId"
              control={control}
              label="Email"
              error={errors?.emailId?.message}
              defaultValue={registrationContext.emailId}
              onBlur={() => {
                if (duplicateUserError) {
                  setDuplicateUserError(false);
                  setOpenInviteError(false);
                }
              }}
            />
            {duplicateUserError && (
              <ErrorContainer>
                <ErrorText>
                  Something went wrong while creating your account. If you have previously
                  registered with Roo, please{" "}
                  <Link linkSize="small" onClick={() => history.push("/login")}>
                    log in
                  </Link>{" "}
                  to continue.
                </ErrorText>
              </ErrorContainer>
            )}
            {openInviteError && (
              <ErrorContainer>
                <ErrorText>
                  It looks like you already have an invite! Check your email to accept the
                  invitation and finish setting up your account.
                </ErrorText>
              </ErrorContainer>
            )}
            <ControlledPasswordInput
              control={control}
              name="password"
              error={errors?.password?.message}
              password={watch("password")}
            />

            <Checkbox
              topAlignedLabel
              label="I agree to receive recurring marketing text messages from Roo at the phone number provided. Msg & data rates may apply. Msg frequency varies. Reply HELP for help and STOP to cancel."
              checked={isMarketingSMSEnabled}
              onChange={() => setIsMarketingSMSEnabled(!isMarketingSMSEnabled)}
              data-testid="marketingSmsToggle"
            />
          </FormFields>
          <ButtonContainer>
            <RegistrationButton
              $buttonType="primary"
              form="registration-form"
              onClick={handleSubmit(onContinue)}
              loading={loading}
              disabled={loading}
            >
              Create My Account
            </RegistrationButton>
          </ButtonContainer>
          <ContainerTopMargin>
            <RegisterCTAText />
          </ContainerTopMargin>
        </Column>
      }
    />
  );
}

const CreateAccountProfileContainer = () => {
  const methods = useForm<RegistrationForm>({
    resolver: zodResolver(getHospitalUserAccountRegistrationFormSchema()),
  });

  useFormPersist(HOSPITAL_REGISTRATION_LOCALSTORAGE_KEY, {
    watch: methods.watch,
    setValue: methods.setValue,
    storage: window.localStorage,
  });

  return (
    <FormProvider {...methods}>
      <CreateAccount />
    </FormProvider>
  );
};
// eslint-disable-next-line no-restricted-exports
export default CreateAccountProfileContainer;
